import { ActionIcon, Box, Group, Progress, ScrollArea, Text, UnstyledButton } from "@mantine/core";
import {
  IconCheck,
  IconCircle,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconX,
} from "@tabler/icons-react";
import { Badge, spacing } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { ReviewImageSummary } from "@models/review";
import "./ReviewSidebar.scss";

/** The two sub-steps shown under the active image. */
export type ReviewSubStep = "q1" | "decision";

interface ReviewSidebarProps {
  images: ReviewImageSummary[];
  /** Index of the active image in `images`. */
  activeIndex: number;
  /** The active sub-step (Q1 or Decision). */
  activeSub: ReviewSubStep;
  /** Whether the active image already has a Q1 score chosen (locally or saved). */
  q1Answered: boolean;
  /** Whether the active image has been decided (approved / rejected). */
  decisionAnswered: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onSelectImage: (index: number) => void;
  onSelectSub: (sub: ReviewSubStep) => void;
}

/** A decided image is approved or rejected; anything else is pending. */
function isDecided(image: ReviewImageSummary): boolean {
  return image.reviewState === "approved" || image.reviewState === "rejected";
}

/**
 * The left sidebar: a Progress header with decided/total and a bar, then a
 * scrollable list of the run's images — a green check when approved, a red x
 * when rejected, an empty circle when pending, labelled `#n · objId`, with an
 * origin chip (reused / redrawn) and the active item highlighted. A reused
 * image is reviewed like a redrawn one (D20 d). The score and the decision are one step in
 * the main panel (owner, 2026-11-04). Collapses to a thin rail
 * showing `N/M done` vertically. Mirrors the reviewer-app `Sidebar`.
 */
export function ReviewSidebar({
  images,
  activeIndex,
  collapsed,
  onToggleCollapsed,
  onSelectImage,
}: ReviewSidebarProps) {
  const { t } = useTranslation();

  const total = images.length;
  const decided = images.filter(isDecided).length;
  const ratio = total === 0 ? 0 : decided / total;

  if (collapsed) {
    return (
      <aside className="review-sidebar review-sidebar--rail">
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onToggleCollapsed}
          aria-label={t("review.sidebar.expand")}
          title={t("review.sidebar.expand")}
        >
          <IconLayoutSidebarLeftExpand size={18} />
        </ActionIcon>
        <Text className="review-sidebar__rail-count" size="xs" fw={500} c="dimmed">
          {t("review.sidebar.doneCount", { decided, total })}
        </Text>
      </aside>
    );
  }

  return (
    <aside className="review-sidebar">
      <Box className="review-sidebar__header">
        <Group justify="space-between" mb={spacing.xs} wrap="nowrap">
          <Text fw={500} size="sm" c="dimmed">
                      {t("review.sidebar.progress")}
                    </Text>
          <Group gap={spacing.xs} wrap="nowrap">
            <Text size="sm" c="dimmed">
              {decided}/{total}
            </Text>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={onToggleCollapsed}
              aria-label={t("review.sidebar.collapse")}
              title={t("review.sidebar.collapse")}
            >
              <IconLayoutSidebarLeftCollapse size={16} />
            </ActionIcon>
          </Group>
        </Group>
        <Progress value={Math.round(ratio * 100)} size="sm" radius="xl" />
      </Box>

      <ScrollArea className="review-sidebar__list" type="auto">
        {images.map((image, index) => {
          const active = index === activeIndex;
          return (
            <div key={`${image.requestId}/${image.objId}`}>
              <UnstyledButton
                className={`review-sidebar__item${active ? " review-sidebar__item--active" : ""}`}
                onClick={() => onSelectImage(index)}
                aria-current={active ? "true" : undefined}
              >
                {image.reviewState === "approved" ? (
                  <IconCheck size={16} className="review-sidebar__icon--approved" aria-hidden="true" />
                ) : image.reviewState === "rejected" ? (
                  <IconX size={16} className="review-sidebar__icon--rejected" aria-hidden="true" />
                ) : (
                  <IconCircle size={16} className="review-sidebar__icon--pending" aria-hidden="true" />
                )}
                <Text size="sm" truncate className="review-sidebar__label">
                  {t("review.sidebar.imageLabel", {
                    n: index + 1,
                    objId: image.objId,
                  })}
                </Text>
                {image.origin && (
                  <Badge
                    className="review-sidebar__origin"
                    size="xs"
                    variant="light"
                    color={image.origin === "reused" ? "blueprint" : "gray"}
                  >
                    {t(`review.images.origin.${image.origin}`, { defaultValue: image.origin })}
                  </Badge>
                )}
              </UnstyledButton>

            </div>
          );
        })}
      </ScrollArea>
    </aside>
  );
}
